import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { View, Text, StyleSheet } from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { ProductFieldInfoVisibility } from "../../../../../view-models/ProductFieldInfoVisibility";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import FloatingDropdownComponent from "../../../common/FloatingDropdownComponent";
import identificationsData from "../../../../../constants/identifications.json";
import { loadCountries } from "../../../../../services/common.service";
import {
  IDENTIFICATION_NUMBER_MAX_LENGTH,
  isIdentificationNumberContentValid,
  isIdentificationNumberFieldKey,
  sanitizeIdentificationNumberInput,
} from "../../../../../helper/identificationNumberValidation";
import { getProductFieldDisplayLabel } from "../../../../../helper/sendValidationHelper";
import { matchesValidationRegEx } from "../../../../../helper/fieldHelper";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  testID?: string;
};

export interface AdditionalInformationStepHandle {
  submit: () => boolean;
}

function buildBillPayAdditionalFieldErrors(
  requiredFields: any[],
  additionalFieldValues: Record<string, any> | null | undefined,
  t: (key: string) => string,
): Record<string, string> {
  const errs: Record<string, string> = {};
  for (const f of requiredFields) {
    const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
    const label = getProductFieldDisplayLabel(f, key);
    const min = Number(f?.FieldMin ?? f?.fieldMin ?? 0);
    const max = Number(f?.FieldMax ?? f?.fieldMax ?? Number.MAX_SAFE_INTEGER);
    const effectiveMax = isIdentificationNumberFieldKey(key)
      ? Math.min(max, IDENTIFICATION_NUMBER_MAX_LENGTH)
      : max;
    const value = String(additionalFieldValues?.[key] ?? "");
    if (!value) errs[key] = `${label} is required`;
    else if (value.length < min) errs[key] = `${label} must be at least ${min} characters`;
    else if (value.length > effectiveMax) {
      errs[key] = `${label} must be at most ${effectiveMax} characters`;
    } else if (
      isIdentificationNumberFieldKey(key) &&
      !isIdentificationNumberContentValid(value)
    ) {
      errs[key] = t("ID_NUMBER_INVALID_CHARS");
    } else if (!matchesValidationRegEx(f, value)) {
      errs[key] = `${label} contains invalid characters`;
    }
  }
  if (isBillPayPhotoIdStateRequired(additionalFieldValues)) {
    const stateVal = String(additionalFieldValues?.senderPhotoIdState ?? "").trim();
    if (!stateVal) {
      errs.senderPhotoIdState = `${t("CHOOSE_STATE_OF_ID")} is required`;
    }
  }
  return errs;
}

function getBillPayAdditionalRequiredFields(
  missingRequiredFields: any[] | null | undefined,
): any[] {
  return (missingRequiredFields || []).filter((f: any) => {
    const visibility = f?.Visibility ?? f?.visibility;
    return visibility === ProductFieldInfoVisibility.REQ || visibility === 0;
  });
}

/** US/CAN photo IDs require issuing state on validate even when GFFP omits the tag. */
export function isBillPayPhotoIdStateRequired(
  additionalFieldValues: Record<string, any> | null | undefined,
): boolean {
  const type = String(additionalFieldValues?.senderPhotoIdType ?? "").trim();
  if (!type) return false;
  const country = String(additionalFieldValues?.senderPhotoIdCountry ?? "").trim();
  if (country === "MEX" || !country) return false;
  return country === "USA" || country === "CAN";
}

/** Shared with MainBillPayScreen handleNext so Continue uses live store values, not stale isStepValid. */
export function areBillPayAdditionalFieldsValid(
  missingRequiredFields: any[] | null | undefined,
  additionalFieldValues: Record<string, any> | null | undefined,
): boolean {
  for (const f of getBillPayAdditionalRequiredFields(missingRequiredFields)) {
    const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
    const min = Number(f?.FieldMin ?? f?.fieldMin ?? 0);
    const max = Number(f?.FieldMax ?? f?.fieldMax ?? Number.MAX_SAFE_INTEGER);
    const effectiveMax = isIdentificationNumberFieldKey(key)
      ? Math.min(max, IDENTIFICATION_NUMBER_MAX_LENGTH)
      : max;
    const value = String(additionalFieldValues?.[key] ?? "");
    if (value.length < min || value.length > effectiveMax || !value) return false;
    if (
      isIdentificationNumberFieldKey(key) &&
      !isIdentificationNumberContentValid(value)
    ) {
      return false;
    }
    if (!matchesValidationRegEx(f, value)) return false;
  }
  if (isBillPayPhotoIdStateRequired(additionalFieldValues)) {
    const stateVal = String(additionalFieldValues?.senderPhotoIdState ?? "").trim();
    if (!stateVal) return false;
  }
  return true;
}

const AdditionalInformationStep = forwardRef<AdditionalInformationStepHandle, Props>(
  ({ onValidationChange, testID }, ref) => {
  const { t } = useTranslation();
  const missingRequiredFields = useSessionStore((s: SessionState) => (s as any).missingRequiredFields) as any[];
  const billPayAllRequiredFieldsStore = useSessionStore(
    (s: SessionState) => (s as any).billPayAllRequiredFieldsStore,
  ) as any[] | null | undefined;
  const setAdditionalFieldValue = useSessionStore((s: SessionState) => (s as any).setAdditionalFieldValue);
  const additionalFieldValues = useSessionStore((s: SessionState) => (s as any).additionalFieldValues) as Record<string, any>;
  const selectedIdTypeKey = useSessionStore((s: SessionState) => (s as any).selectedIdTypeKey) as string | null | undefined;
  const setSelectedIdTypeKey = useSessionStore((s: SessionState) => (s as any).setSelectedIdTypeKey);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);
  const [idTypes, setIdTypes] = useState<Array<{ key: string; EnglishLabel: string; SpanishLabel: string; Value?: string; photoIdCountry?: string; photoIdState?: string }>>([]);
  const [availableStates, setAvailableStates] = useState<Array<{ stateid: number; stateAbbrivation: string; stateName: string }>>([]);

  const requiredFields = useMemo(() => {
    const fromMissing = getBillPayAdditionalRequiredFields(missingRequiredFields);
    if (fromMissing.length > 0) return fromMissing;
    const extraKeys = Object.keys(additionalFieldValues || {});
    if (extraKeys.length === 0) return fromMissing;
    const allReq = getBillPayAdditionalRequiredFields(billPayAllRequiredFieldsStore);
    return allReq.filter((f: any) => {
      const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
      return extraKeys.includes(key);
    });
  }, [missingRequiredFields, additionalFieldValues, billPayAllRequiredFieldsStore]);

  const showPhotoIdStateField = useMemo(() => {
    if (isBillPayPhotoIdStateRequired(additionalFieldValues)) return true;
    return (missingRequiredFields || []).some((f: any) => {
      const k = String(f?.XmlTag ?? f?.xmlTag ?? "");
      return k === "senderPhotoIdType";
    });
  }, [missingRequiredFields, additionalFieldValues]);

  // If senderLegalIdType is required, auto-set to SSN
  useEffect(() => {
    const hasSenderLegalIdType = (missingRequiredFields || []).some((f: any) => String(f?.XmlTag ?? f?.xmlTag ?? "") === 'senderLegalIdType');
    if (hasSenderLegalIdType) {
      try { setAdditionalFieldValue?.('senderLegalIdType', 'SSN'); } catch {}
    }
  }, [missingRequiredFields]);

  const computeFieldErrors = useCallback(
    () => buildBillPayAdditionalFieldErrors(requiredFields, additionalFieldValues, t),
    [requiredFields, additionalFieldValues, t],
  );

  useEffect(() => {
    onValidationChange?.(
      areBillPayAdditionalFieldsValid(missingRequiredFields, additionalFieldValues),
    );
  }, [requiredFields, additionalFieldValues, missingRequiredFields, onValidationChange]);

  useEffect(() => {
    if (!showFieldErrors) return;
    setFieldErrors(computeFieldErrors());
  }, [showFieldErrors, computeFieldErrors]);

  useImperativeHandle(ref, () => ({
    submit: () => {
      const errs = computeFieldErrors();
      setFieldErrors(errs);
      setShowFieldErrors(true);
      return areBillPayAdditionalFieldsValid(missingRequiredFields, additionalFieldValues);
    },
  }));

  useEffect(() => {
    const raw: any = identificationsData as any;
    let list: Array<{ key: string; EnglishLabel: string; SpanishLabel: string; Value?: string; photoIdCountry?: string; photoIdState?: string }> = [];
    if (Array.isArray(raw?.IDTypes)) {
      list = (raw.IDTypes as any[]).map((x: any) => ({
        key: x.key ?? x.IDkey ?? x.Value,
        EnglishLabel: x.EnglishLabel,
        SpanishLabel: x.SpanishLabel,
        Value: x.Value,
        photoIdCountry: x.photoIdCountry,
        photoIdState: x.photoIdState,
      }));
    } else if (raw && typeof raw === "object") {
      list = Object.entries(raw).map(([key, v]: [string, any]) => ({
        key,
        EnglishLabel: v?.EnglishLabel,
        SpanishLabel: v?.SpanishLabel,
        Value: v?.Value,
        photoIdCountry: v?.photoIdCountry,
        photoIdState: v?.photoIdState,
      }));
    }
    setIdTypes(list.filter(x => x && x.key));
  }, []);

  // Derive selected ID Type — Value alone is not unique (e.g. US + Canadian both use "DRV").
  const selectedIdType = useMemo(() => {
    const storedKey = String(selectedIdTypeKey ?? "").trim();
    if (storedKey) {
      const byKey = idTypes.find((t) => t.key === storedKey);
      if (byKey) return byKey;
    }

    const selectedValue = String(additionalFieldValues?.["senderPhotoIdType"] ?? "");
    if (!selectedValue) return null;

    const selectedCountry = String(additionalFieldValues?.["senderPhotoIdCountry"] ?? "");
    if (selectedCountry) {
      const byValueAndCountry = idTypes.find(
        (t) =>
          String(t.Value ?? "") === selectedValue &&
          String(t.photoIdCountry ?? "") === selectedCountry,
      );
      if (byValueAndCountry) return byValueAndCountry;
    }

    return idTypes.find((t) => String(t.Value ?? "") === selectedValue) ?? null;
  }, [idTypes, additionalFieldValues, selectedIdTypeKey]);

  // When ID type changes, auto-set photoIdCountry and handle state logic
  useEffect(() => {
    const country = String(
      selectedIdType?.photoIdCountry ??
        additionalFieldValues?.senderPhotoIdCountry ??
        "",
    );
    if (country && selectedIdType?.photoIdCountry) {
      try { setAdditionalFieldValue?.('senderPhotoIdCountry', country); } catch {}
    }

    const handleStates = async () => {
      if (country === 'MEX') {
        // Auto-set default state from definition and do not show the state field
        const defaultState = String(selectedIdType?.photoIdState ?? 'AG');
        try { setAdditionalFieldValue?.('senderPhotoIdState', defaultState); } catch {}
        setAvailableStates([]);
        setOpenDropdownKey(null);
        return;
      }
      if (country === 'USA' || country === 'CAN') {
        try {
          const result: any = await loadCountries();
          const list: any[] = (result?.data ?? []) as any[];
          const countryObj = list.find((c: any) => c?.CountryCode === country);
          const states: Array<{ stateid: number; stateAbbrivation: string; stateName: string }> = Array.isArray(countryObj?.States) ? countryObj.States : [];
          setAvailableStates(states);
          return;
        } catch (e) {
          console.error('Failed to load states for', country, e);
          setAvailableStates([]);
        }
      }
      setAvailableStates([]);
    };

    handleStates();
  }, [selectedIdType, additionalFieldValues?.senderPhotoIdCountry, setAdditionalFieldValue]);

  const handleChange = (key: string, text: string) => {
    const v = isIdentificationNumberFieldKey(key)
      ? sanitizeIdentificationNumberInput(text)
      : text;
    setAdditionalFieldValue?.(key, v);
  };

  return (
    <View style={styles.container} testID={testID}>
      <Text style={[styles.title, { paddingRight: 100 }]}>{t("WE_NEED_FEW_MORE_DETAILS")}</Text>
      {requiredFields.map((f: any) => {
        const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
        const label = getProductFieldDisplayLabel(f, key);
        const placeholder = label;
        const value = String((additionalFieldValues?.[key] ?? ""));
        const error = showFieldErrors ? fieldErrors[key] : undefined;
        // Country is derived from the selected ID type; do not render this field even if required
        if (key === 'senderPhotoIdCountry') {
          return null;
        }
        if (key === 'senderPhotoIdType') {
          const idTypeOptions = idTypes.map((opt) => ({
            value: opt.key,
            label: opt.EnglishLabel,
          }));
          return (
            <View key={key} style={styles.fieldWrapper}>
              <FloatingDropdownComponent
                testID="senderPhotoIdTypeButton"
                optionTestID="senderPhotoIdTypeOption"
                label={label}
                value={selectedIdType?.key ?? ""}
                displayValue={selectedIdType?.EnglishLabel}
                options={idTypeOptions}
                isOpen={openDropdownKey === key}
                onOpenChange={(open) => setOpenDropdownKey(open ? key : null)}
                onSelect={(selectedKey) => {
                  const opt = idTypes.find((x) => x.key === selectedKey);
                  if (!opt) return;
                  try { setSelectedIdTypeKey?.(selectedKey); } catch {}
                  setAdditionalFieldValue?.(key, String(opt.Value ?? ""));
                  if (opt.photoIdCountry) {
                    setAdditionalFieldValue?.('senderPhotoIdCountry', String(opt.photoIdCountry));
                  }
                  if (opt.photoIdCountry === 'MEX') {
                    const defaultState = String(opt.photoIdState ?? 'AG');
                    setAdditionalFieldValue?.('senderPhotoIdState', defaultState);
                  } else {
                    setAdditionalFieldValue?.('senderPhotoIdState', '');
                  }
                  setOpenDropdownKey(null);
                }}
                errorText={error}
              />
            </View>
          );
        }
        if (key === 'senderPhotoIdState') {
          const country = String(selectedIdType?.photoIdCountry ?? additionalFieldValues?.['senderPhotoIdCountry'] ?? "");
          if (country === 'MEX') {
            return null;
          }
          if (country === 'USA' || country === 'CAN') {
            const selectedState = availableStates.find((s) => String(s.stateAbbrivation) === value);
            const stateOptions = availableStates.map((opt) => ({
              value: String(opt.stateAbbrivation),
              label: opt.stateName,
            }));
            return (
              <View key={key} style={styles.fieldWrapper}>
                <FloatingDropdownComponent
                  testID="senderPhotoIdStateButton"
                  optionTestID="senderPhotoIdStateOption"
                  label={label}
                  value={value}
                  displayValue={selectedState?.stateName}
                  options={stateOptions}
                  isOpen={openDropdownKey === key}
                  onOpenChange={(open) => setOpenDropdownKey(open ? key : null)}
                  onSelect={(selected) => setAdditionalFieldValue?.(key, selected)}
                  errorText={error}
                />
              </View>
            );
          }
          return null;
        }
        // Render senderLegalIdType as read-only when present and keep its value as SSN
        if (key === 'senderLegalIdType') {
          const fixedValue = 'SSN';
          return (
            <View key={key} style={styles.fieldWrapper}>
              <FloatingTextInputComponent
                testID={`billPayAdditionalField_${key}`}
                label={placeholder}
                value={fixedValue}
                onChangeText={() => {}}
                editable={false}
              />
              {!!error && <Text style={styles.error}>{error}</Text>}
            </View>
          );
        }
        return (
          <View key={key} style={styles.fieldWrapper}>
            <FloatingTextInputComponent
              testID={`billPayAdditionalField_${key}`}
              label={placeholder}
              value={value}
              isAutoFocus={false}
              onChangeText={(text) => handleChange(key, text)}
              maxLength={isIdentificationNumberFieldKey(key) ? IDENTIFICATION_NUMBER_MAX_LENGTH : undefined}
            />
            {!!error && <Text style={styles.error}>{error}</Text>}
          </View>
        );
      })}
      {/* Conditionally render state when ID type is asked and country is USA/CAN, even if not in missingRequiredFields */}
      {(() => {
        const country = String(selectedIdType?.photoIdCountry ?? additionalFieldValues?.['senderPhotoIdCountry'] ?? "");
        if (!showPhotoIdStateField) return null;
        if (country !== 'USA' && country !== 'CAN') return null;
        const key = 'senderPhotoIdState';
        const value = String((additionalFieldValues?.[key] ?? ""));
        const selectedState = availableStates.find((s) => String(s.stateAbbrivation) === value);
        const stateOptions = availableStates.map((opt) => ({
          value: String(opt.stateAbbrivation),
          label: opt.stateName,
        }));
        const stateError = showFieldErrors ? fieldErrors[key] : undefined;
        return (
          <View key={key + '-extra'} style={styles.fieldWrapper}>
            <FloatingDropdownComponent
              testID="senderPhotoIdStateButton"
              optionTestID="senderPhotoIdStateOption"
              label={t("CHOOSE_STATE_OF_ID")}
              value={value}
              displayValue={selectedState?.stateName}
              options={stateOptions}
              isOpen={openDropdownKey === key}
              onOpenChange={(open) => setOpenDropdownKey(open ? key : null)}
              onSelect={(selected) => setAdditionalFieldValue?.(key, selected)}
              errorText={stateError}
            />
          </View>
        );
      })()}
    </View>
  );
});

AdditionalInformationStep.displayName = "AdditionalInformationStep";

const styles = StyleSheet.create({
  container: { paddingHorizontal: 24, marginTop: 16, backgroundColor: Colors.white },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
  },
  subtitle: { fontSize: 14, color: "#555" },
  fieldWrapper: { marginBottom: 16 },
  error: { color: "#E11D48", marginTop: 6, fontSize: 12 },
});

export default AdditionalInformationStep;

